import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowLeft, Image, FileText, Plus, Minus } from 'lucide-react'

interface CreationSettingsProps {
  onNext: () => void
  onPrevious: () => void
}

const CreationSettings = ({ onNext, onPrevious }: CreationSettingsProps) => {
  const [contentType, setContentType] = React.useState<'image-only' | 'image-text'>('image-text')
  const [pageCount, setPageCount] = React.useState(5)

  return (
    <div className="w-full">
      <Card className="p-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-gray-800 mb-2">创作设置</h2>
          <p className="text-sm text-gray-500 mb-8">
            设置绘本的内容形式和页数。
          </p>

          <div className="space-y-8">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                内容形式
              </label>
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant={contentType === 'image-text' ? 'default' : 'outline'}
                  className={`flex items-center gap-2 flex-1 transition-colors ${
                    contentType === 'image-text' 
                      ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setContentType('image-text')}
                >
                  <FileText className="w-4 h-4" />
                  <span>图文结合</span>
                </Button>
                <Button
                  type="button"
                  variant={contentType === 'image-only' ? 'default' : 'outline'}
                  className={`flex items-center gap-2 flex-1 transition-colors ${
                    contentType === 'image-only' 
                      ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setContentType('image-only')}
                >
                  <Image className="w-4 h-4" />
                  <span>仅包含图片</span>
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                {contentType === 'image-text' 
                  ? '每页包含配图和文字描述' 
                  : '每页仅包含精美配图'}
              </p>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                绘本页数
              </label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setPageCount(Math.max(1, pageCount - 1))}
                  disabled={pageCount <= 1}
                  className="rounded-full w-8 h-8"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-2xl font-bold text-gray-800">{pageCount}</span>
                  <span className="text-sm text-gray-500 ml-2">页</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setPageCount(Math.min(10, pageCount + 1))}
                  disabled={pageCount >= 10}
                  className="rounded-full w-8 h-8"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                可选择1-10页，建议5-8页最为适合
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center mt-10">
            <Button
              variant="outline"
              onClick={onPrevious}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>上一步</span>
            </Button>

            <Button
              onClick={onNext}
              className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90"
            >
              <span>开始创作</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default CreationSettings 